import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui';
import {
  TERMS_TITLE,
  TERMS_INTRO,
  TERMS_POINTS,
  TERMS_AGREEMENT_LABEL,
} from '../terms';
import {
  setupAssentSignature,
  setupTypedNameSignature,
  setupPinSignature,
  setupBiometricSignature,
  canUseBiometric,
} from '../services/signingService';

// ── Steps ───────────────────────────────────────────────────────────────────
const STEP_TERMS = 'terms';
const STEP_NAME = 'name';
const STEP_METHOD = 'method';
const STEP_ASSENT = 'assent';
const STEP_PIN = 'pin';
const STEP_BIOMETRIC = 'biometric';
const STEP_TYPED = 'typed';
const STEP_DONE = 'done';

const METHODS = {
  assent: {
    id: 'assent',
    title: 'Just tap',
    body: 'Tapping Sign while logged in is your signature — no code to remember.',
    icon: '✓',
  },
  typed_name: {
    id: 'typed_name',
    title: 'Type your name',
    body: 'Each time you sign, type your full legal name to confirm.',
    icon: '✍︎',
  },
  biometric: {
    id: 'biometric',
    title: 'Face or fingerprint',
    body: 'Use your device\'s built-in biometric to sign in one tap.',
    icon: '☉',
  },
  pin: {
    id: 'pin',
    title: '4-digit PIN',
    body: 'Choose a private 4-digit code only you know.',
    icon: '◉',
  },
};

// ── Component ───────────────────────────────────────────────────────────────
const SignatureSetup = ({ onClose, onComplete }) => {
  const [step, setStep] = useState(STEP_TERMS);
  const [agreed, setAgreed] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [biometricAvailable, setBiometricAvailable] = useState(null);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    canUseBiometric().then((ok) => {
      if (active) setBiometricAvailable(ok);
    });
    return () => {
      active = false;
    };
  }, []);

  const trimmedName = legalName.trim();
  const nameValid = useMemo(() => {
    const parts = trimmedName.split(/\s+/);
    return parts.length >= 2 && parts.every((p) => p.length >= 2);
  }, [trimmedName]);

  const reset = () => {
    setError('');
  };

  // ── Submit handlers ───────────────────────────────────────────────────────
  const submitTyped = async () => {
    setSubmitting(true);
    setError('');
    try {
      await setupTypedNameSignature({ legalName: trimmedName });
      setStep(STEP_DONE);
    } catch (e) {
      setError(e.message || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAssent = async () => {
    setSubmitting(true);
    setError('');
    try {
      await setupAssentSignature({ legalName: trimmedName });
      setStep(STEP_DONE);
    } catch (e) {
      setError(e.message || 'Could not save your signature.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitBiometric = async () => {
    setSubmitting(true);
    setError('');
    try {
      await setupBiometricSignature({ legalName: trimmedName });
      setStep(STEP_DONE);
    } catch (e) {
      const msg = e?.message || '';
      if (/cancel|abort|NotAllowed/i.test(msg)) {
        setError('Setup was cancelled. Try again when you\'re ready.');
      } else {
        setError(msg || 'Could not set up biometric signing.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitPin = async () => {
    if (pin.length !== 4) {
      setError('Your PIN must be exactly 4 digits.');
      return;
    }
    if (pin !== pinConfirm) {
      setError('Those PINs don\'t match.');
      return;
    }
    if (/^(\d)\1{3}$/.test(pin) || pin === '1234' || pin === '4321') {
      setError('Choose a less obvious PIN.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await setupPinSignature({ legalName: trimmedName, pin });
      setStep(STEP_DONE);
    } catch (e) {
      setError(e.message || 'Could not save your PIN.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-base-300/80 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-base-100 rounded-2xl shadow-2xl border border-base-300 overflow-hidden">
        <div className="px-8 pt-8 pb-2 border-b border-base-200">
          <div className="text-xs uppercase tracking-[0.2em] text-base-content/50 mb-1">
            Swan Schools · Signature
          </div>
          <h2 className="text-2xl font-serif font-semibold text-base-content">
            {step === STEP_DONE ? 'Your signature is ready' : TERMS_TITLE}
          </h2>
        </div>

        <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
          {step === STEP_TERMS && (
            <TermsStep
              agreed={agreed}
              setAgreed={setAgreed}
              onContinue={() => {
                reset();
                setStep(STEP_NAME);
              }}
              onClose={onClose}
            />
          )}

          {step === STEP_NAME && (
            <NameStep
              legalName={legalName}
              setLegalName={setLegalName}
              valid={nameValid}
              onBack={() => setStep(STEP_TERMS)}
              onContinue={() => {
                reset();
                setStep(STEP_METHOD);
              }}
            />
          )}

          {step === STEP_METHOD && (
            <MethodStep
              biometricAvailable={biometricAvailable}
              onBack={() => setStep(STEP_NAME)}
              onPick={(method) => {
                reset();
                if (method === 'assent') setStep(STEP_ASSENT);
                if (method === 'typed_name') setStep(STEP_TYPED);
                if (method === 'biometric') setStep(STEP_BIOMETRIC);
                if (method === 'pin') setStep(STEP_PIN);
              }}
            />
          )}

          {step === STEP_ASSENT && (
            <AssentStep
              legalName={trimmedName}
              error={error}
              submitting={submitting}
              onBack={() => setStep(STEP_METHOD)}
              onConfirm={submitAssent}
            />
          )}

          {step === STEP_TYPED && (
            <TypedStep
              legalName={trimmedName}
              error={error}
              submitting={submitting}
              onBack={() => setStep(STEP_METHOD)}
              onConfirm={submitTyped}
            />
          )}

          {step === STEP_BIOMETRIC && (
            <BiometricStep
              error={error}
              submitting={submitting}
              onBack={() => setStep(STEP_METHOD)}
              onConfirm={submitBiometric}
            />
          )}

          {step === STEP_PIN && (
            <PinStep
              pin={pin}
              setPin={setPin}
              pinConfirm={pinConfirm}
              setPinConfirm={setPinConfirm}
              error={error}
              submitting={submitting}
              onBack={() => setStep(STEP_METHOD)}
              onConfirm={submitPin}
            />
          )}

          {step === STEP_DONE && (
            <DoneStep
              legalName={trimmedName}
              onClose={() => {
                if (onComplete) onComplete();
                if (onClose) onClose();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Step components ─────────────────────────────────────────────────────────

const TermsStep = ({ agreed, setAgreed, onContinue, onClose }) => (
  <div className="space-y-5">
    <p className="text-base-content/80 leading-relaxed">{TERMS_INTRO}</p>

    <div className="space-y-4">
      {TERMS_POINTS.map((point) => (
        <div key={point.heading} className="border-l-2 border-primary/40 pl-4">
          <div className="font-semibold text-base-content mb-1">{point.heading}</div>
          <div className="text-sm text-base-content/70 leading-relaxed">{point.body}</div>
        </div>
      ))}
    </div>

    <label className="flex items-start gap-3 p-4 rounded-lg bg-base-200/50 border border-base-300 cursor-pointer hover:bg-base-200 transition-colors">
      <input
        type="checkbox"
        checked={agreed}
        onChange={(e) => setAgreed(e.target.checked)}
        className="checkbox checkbox-primary mt-0.5"
      />
      <span className="text-sm font-medium text-base-content leading-relaxed">
        {TERMS_AGREEMENT_LABEL}
      </span>
    </label>

    <div className="flex justify-between items-center pt-2">
      <Button variant="ghost" onClick={onClose}>
        Not now
      </Button>
      <Button variant="primary" disabled={!agreed} onClick={onContinue}>
        Continue
      </Button>
    </div>
  </div>
);

const NameStep = ({ legalName, setLegalName, valid, onBack, onContinue }) => (
  <div className="space-y-5">
    <div>
      <div className="font-semibold text-base-content mb-1">Your full legal name</div>
      <p className="text-sm text-base-content/70 leading-relaxed">
        This is the name that will appear on every document you sign. Use the
        name on your ID — first and last, exactly as it's written.
      </p>
    </div>

    <input
      type="text"
      value={legalName}
      onChange={(e) => setLegalName(e.target.value)}
      placeholder="e.g. Sarah Mokoena"
      autoComplete="name"
      className="input input-bordered w-full text-lg font-serif h-14"
      autoFocus
    />

    {legalName && !valid && (
      <p className="text-xs text-warning">
        Please enter at least a first and last name.
      </p>
    )}

    <div className="flex justify-between items-center pt-2">
      <Button variant="ghost" onClick={onBack}>
        Back
      </Button>
      <Button variant="primary" disabled={!valid} onClick={onContinue}>
        Continue
      </Button>
    </div>
  </div>
);

const MethodStep = ({ biometricAvailable, onBack, onPick }) => (
  <div className="space-y-5">
    <div>
      <div className="font-semibold text-base-content mb-1">Choose how you'll sign</div>
      <p className="text-sm text-base-content/70 leading-relaxed">
        Whichever you pick is your signature. You can change this later from
        your account settings.
      </p>
    </div>

    <div className="space-y-3">
      <MethodCard method={METHODS.assent} onClick={() => onPick('assent')} />
      <MethodCard method={METHODS.typed_name} onClick={() => onPick('typed_name')} />
      <MethodCard
        method={METHODS.biometric}
        onClick={() => onPick('biometric')}
        disabled={biometricAvailable === false}
        note={
          biometricAvailable === false
            ? 'Not available on this device.'
            : biometricAvailable === null
              ? 'Checking…'
              : null
        }
      />
      <MethodCard method={METHODS.pin} onClick={() => onPick('pin')} />
    </div>

    <div className="flex justify-between items-center pt-2">
      <Button variant="ghost" onClick={onBack}>
        Back
      </Button>
    </div>
  </div>
);

const MethodCard = ({ method, onClick, disabled, note }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left p-4 rounded-xl border transition-all ${
      disabled
        ? 'border-base-300 bg-base-200/30 opacity-60 cursor-not-allowed'
        : 'border-base-300 hover:border-primary hover:bg-primary/5 cursor-pointer'
    }`}
  >
    <div className="flex items-start gap-4">
      <div className="text-2xl text-primary leading-none mt-0.5">{method.icon}</div>
      <div className="flex-1">
        <div className="font-semibold text-base-content">{method.title}</div>
        <div className="text-sm text-base-content/70 mt-0.5">{method.body}</div>
        {note && <div className="text-xs text-base-content/50 mt-1">{note}</div>}
      </div>
    </div>
  </button>
);

const AssentStep = ({ legalName, error, submitting, onBack, onConfirm }) => (
  <div className="space-y-5">
    <div>
      <div className="font-semibold text-base-content mb-1">Confirm your signature</div>
      <p className="text-sm text-base-content/70 leading-relaxed">
        From now on, tapping Sign while logged in confirms your agreement as{' '}
        <span className="font-serif font-medium">{legalName}</span>.
      </p>
    </div>
    {error && <div className="text-sm text-error">{error}</div>}
    <div className="flex justify-between items-center pt-2">
      <Button variant="ghost" onClick={onBack} disabled={submitting}>Back</Button>
      <Button variant="primary" loading={submitting} onClick={onConfirm}>
        Save signature
      </Button>
    </div>
  </div>
);

const TypedStep = ({ legalName, error, submitting, onBack, onConfirm }) => (
  <div className="space-y-5">
    <div>
      <div className="font-semibold text-base-content mb-1">Confirm your signature</div>
      <p className="text-sm text-base-content/70 leading-relaxed">
        From now on, when you sign, you'll type this name to confirm.
      </p>
    </div>

    <div className="p-6 rounded-xl border-2 border-primary/20 bg-primary/5 text-center">
      <div className="text-xs uppercase tracking-widest text-base-content/50 mb-2">
        Your signature
      </div>
      <div className="font-serif text-2xl text-base-content">{legalName}</div>
    </div>

    {error && <div className="text-sm text-error">{error}</div>}

    <div className="flex justify-between items-center pt-2">
      <Button variant="ghost" onClick={onBack} disabled={submitting}>
        Back
      </Button>
      <Button variant="primary" loading={submitting} onClick={onConfirm}>
        Save signature
      </Button>
    </div>
  </div>
);

const BiometricStep = ({ error, submitting, onBack, onConfirm }) => (
  <div className="space-y-5">
    <div>
      <div className="font-semibold text-base-content mb-1">Set up biometric signing</div>
      <p className="text-sm text-base-content/70 leading-relaxed">
        Your device will ask for your fingerprint or face to register this as
        your signature. Nothing biometric leaves your device — we only store a
        cryptographic key that proves it's you.
      </p>
    </div>

    <div className="p-6 rounded-xl border-2 border-primary/20 bg-primary/5 text-center">
      <div className="text-4xl text-primary mb-2">☉</div>
      <div className="text-sm text-base-content/70">
        Click below, then complete the prompt on your device.
      </div>
    </div>

    {error && <div className="text-sm text-error">{error}</div>}

    <div className="flex justify-between items-center pt-2">
      <Button variant="ghost" onClick={onBack} disabled={submitting}>
        Back
      </Button>
      <Button variant="primary" loading={submitting} onClick={onConfirm}>
        Set up
      </Button>
    </div>
  </div>
);

const PinStep = ({ pin, setPin, pinConfirm, setPinConfirm, error, submitting, onBack, onConfirm }) => {
  const sanitize = (v) => v.replace(/\D/g, '').slice(0, 4);
  return (
    <div className="space-y-5">
      <div>
        <div className="font-semibold text-base-content mb-1">Choose a 4-digit PIN</div>
        <p className="text-sm text-base-content/70 leading-relaxed">
          Make it private and not easy to guess. Don't reuse a PIN you use for
          banking. You'll enter this each time you sign.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label py-1">
            <span className="label-text text-sm">New PIN</span>
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin}
            onChange={(e) => setPin(sanitize(e.target.value))}
            className="input input-bordered w-full font-mono tracking-[0.5em] text-center text-xl h-14"
            placeholder="····"
            maxLength={4}
            autoFocus
          />
        </div>
        <div>
          <label className="label py-1">
            <span className="label-text text-sm">Confirm PIN</span>
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pinConfirm}
            onChange={(e) => setPinConfirm(sanitize(e.target.value))}
            className="input input-bordered w-full font-mono tracking-[0.5em] text-center text-xl h-14"
            placeholder="····"
            maxLength={4}
          />
        </div>
      </div>

      {error && <div className="text-sm text-error">{error}</div>}

      <div className="flex justify-between items-center pt-2">
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button
          variant="primary"
          loading={submitting}
          disabled={pin.length !== 4 || pinConfirm.length !== 4}
          onClick={onConfirm}
        >
          Save PIN
        </Button>
      </div>
    </div>
  );
};

const DoneStep = ({ legalName, onClose }) => (
  <div className="space-y-5 text-center py-4">
    <div className="text-5xl">✓</div>
    <div>
      <div className="font-semibold text-base-content text-lg mb-1">
        You're all set
      </div>
      <p className="text-sm text-base-content/70 leading-relaxed max-w-sm mx-auto">
        Your signature is registered as <span className="font-serif">{legalName}</span>.
        Next time you're asked to sign, you'll just confirm with your chosen
        method.
      </p>
    </div>
    <div className="pt-2">
      <Button variant="primary" onClick={onClose}>
        Done
      </Button>
    </div>
  </div>
);

export default SignatureSetup;
